<div class="schedule-grid" [class.loading]="showLoader" [class.sg-compact]="isCompactMode" [class.sg-mobile]="isMobile">

  <!-- Loader overlay -->
  <div class="sg-loader" *ngIf="showLoader">
    <div class="spinner-border" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- ═══ Toolbar ═══ -->
  <div class="sg-toolbar">
    <div class="sg-toolbar__nav">
      <button class="btn btn-sm btn-action-outline" (click)="navigatePrevious()"
        [attr.aria-label]="'ScheduleGrid.previous' | translate">
        <i class="bi bi-chevron-left"></i>
      </button>
      <button class="btn btn-sm btn-action-outline" (click)="navigateToday()">
        {{ 'ScheduleGrid.today' | translate }}
      </button>
      <button class="btn btn-sm btn-action-outline" (click)="navigateNext()"
        [attr.aria-label]="'ScheduleGrid.next' | translate">
        <i class="bi bi-chevron-right"></i>
      </button>
    </div>

    <h6 class="sg-toolbar__title mb-0">{{ periodLabel }}</h6>

    <div class="sg-toolbar__modes">
      <div class="btn-group btn-group-sm">
        <button class="btn" [class.btn-dark]="viewMode === 'week'" [class.btn-action-outline]="viewMode !== 'week'"
          (click)="changeViewMode('week')">
          {{ 'ScheduleGrid.week' | translate }}
        </button>
        <button class="btn" [class.btn-dark]="viewMode === 'two-weeks'"
          [class.btn-action-outline]="viewMode !== 'two-weeks'" (click)="changeViewMode('two-weeks')">
          {{ 'ScheduleGrid.twoWeeks' | translate }}
        </button>
        <button class="btn" *ngIf="!isMobile" [class.btn-dark]="viewMode === 'month'"
          [class.btn-action-outline]="viewMode !== 'month'" (click)="changeViewMode('month')">
          {{ 'ScheduleGrid.month' | translate }}
        </button>
        <button class="btn" *ngIf="!isMobile" [class.btn-dark]="viewMode === 'one-month'"
          [class.btn-action-outline]="viewMode !== 'one-month'" (click)="changeViewMode('one-month')">
          {{ 'ScheduleGrid.oneMonth' | translate }}
        </button>
        <button class="btn" *ngIf="!isMobile" [class.btn-dark]="viewMode === 'three-months'"
          [class.btn-action-outline]="viewMode !== 'three-months'" (click)="changeViewMode('three-months')">
          {{ 'ScheduleGrid.threeMonths' | translate }}
        </button>
      </div>

      <!-- Slot for extra toolbar content from parent -->
      <ng-content select="[schedule-grid-toolbar]"></ng-content>
    </div>
  </div>

  <!-- ═══ Scroll container ═══ -->
  <div class="sg-scroll-container">
    <table class="sg-table">

      <!-- ── Month header row (for two-weeks & month) ── -->
      <thead *ngIf="showMonthHeader && monthSpans.length > 1">
        <tr class="sg-header sg-header--month">
          <th class="sg-header__label" [style.width.px]="effectiveLabelWidth" [style.min-width.px]="effectiveLabelWidth"></th>
          <th *ngFor="let ic of infoColumns" [style.width.px]="ic.width || 80"></th>
          <th *ngFor="let ms of monthSpans" [attr.colspan]="ms.colSpan" class="sg-header__month-cell">
            {{ ms.label }}
          </th>
        </tr>
      </thead>

      <!-- ── Week number header row ── -->
      <thead *ngIf="showWeekNumbers">
        <tr class="sg-header sg-header--week">
          <th class="sg-header__label" [style.width.px]="effectiveLabelWidth" [style.min-width.px]="effectiveLabelWidth"></th>
          <th *ngFor="let ic of infoColumns" [style.width.px]="ic.width || 80"></th>
          <th *ngFor="let ws of weekSpans" [attr.colspan]="ws.colSpan" class="sg-header__week-cell">
            {{ 'ScheduleGrid.weekShort' | translate }} {{ ws.weekNumber }}
          </th>
        </tr>
      </thead>

      <!-- ── Day header row ── -->
      <thead>
        <tr class="sg-header sg-header--day">
          <th class="sg-header__label sg-header__label--sticky" [style.width.px]="effectiveLabelWidth" [style.min-width.px]="effectiveLabelWidth">
            <!-- empty / label header -->
          </th>
          <th *ngFor="let ic of infoColumns" class="sg-header__info" [style.width.px]="ic.width || 80">
            {{ ic.title }}
          </th>
          <th *ngFor="let day of dayColumns; trackBy: trackByDay"
            class="sg-header__day-cell"
            [class.sg-weekend]="day.isWeekend"
            [class.sg-today]="highlightToday && day.isToday">
            <span class="sg-header__day-name">{{ day.dayName }}</span>
            <span class="sg-header__day-num" [class.sg-today-badge]="highlightToday && day.isToday">
              {{ day.dayOfMonth }}
            </span>
          </th>
        </tr>
      </thead>

      <!-- ═══ Body ═══ -->
      <tbody>
        <ng-container *ngFor="let section of groupedRows; trackBy: trackByRowId">

          <!-- Group header row -->
          <tr *ngIf="section.group" class="sg-group-row" (click)="collapsibleGroups && toggleGroup(section.group!.id)">
            <td [attr.colspan]="1 + infoColumns.length + dayColumns.length" class="sg-group-cell">
              <div class="sg-group-header">
                <i class="bi" [ngClass]="section.collapsed ? 'bi-chevron-right' : 'bi-chevron-down'"
                  *ngIf="collapsibleGroups"></i>
                <strong>{{ section.group!.label }}</strong>
                <span class="sg-group-count badge text-bg-secondary ms-2">{{ section.rows.length }}</span>
              </div>
            </td>
          </tr>

          <!-- Data rows (hidden if group is collapsed) -->
          <ng-container *ngIf="!section.collapsed">
            <tr *ngFor="let row of section.rows; trackBy: trackByRow" class="sg-row">
              <!-- Label cell (sticky left) -->
              <td class="sg-row__label sg-row__label--sticky" [style.width.px]="effectiveLabelWidth" [style.min-width.px]="effectiveLabelWidth">
                <ng-container *ngIf="templateMap.has('rowLabel'); else defaultRowLabel">
                  <ng-container *ngTemplateOutlet="templateMap.get('rowLabel')!.template; context: { $implicit: row }">
                  </ng-container>
                </ng-container>
                <ng-template #defaultRowLabel>
                  <div class="sg-row-label">
                    <span class="sg-row-label__name">{{ row.label }}</span>
                    <span class="sg-row-label__sub" *ngIf="row.sublabel">{{ row.sublabel }}</span>
                  </div>
                </ng-template>
              </td>

              <!-- Info columns -->
              <td *ngFor="let ic of infoColumns" class="sg-row__info">
                {{ getInfoValue(row, ic.field) }}
              </td>

              <!-- Day cells -->
              <td *ngFor="let day of dayColumns; let di = index; trackBy: trackByDay"
                class="sg-cell"
                [class.sg-weekend]="day.isWeekend"
                [class.sg-today]="highlightToday && day.isToday"
                [class.sg-cell--drag-selected]="isCellInDragRange(row, di)"
                (mousedown)="onCellMouseDown($event, row, day)"
                (mouseenter)="onCellMouseEnter(row, day)"
                (click)="onCellClick(row, day)">

                <ng-container *ngIf="getBlocks(row, day.isoDate) as blocks">
                  <ng-container *ngIf="blocks.length > 0; else emptyCell">
                    <div *ngFor="let block of blocks; let bi = index"
                      class="sg-block"
                      [ngClass]="[block.cssClass || '', isCompactMode ? 'sg-block--compact' : '']"
                      [style.background-color]="getBlockColor(block, bi)"
                      [title]="getBlockTooltip(block)"
                      (click)="onBlockClick($event, row, day, block)">

                      <ng-container *ngIf="!isCompactMode">
                        <ng-container *ngIf="templateMap.has('blockContent'); else defaultBlock">
                          <ng-container *ngTemplateOutlet="templateMap.get('blockContent')!.template;
                            context: { $implicit: block, row: row, date: day.date }">
                          </ng-container>
                        </ng-container>
                        <ng-template #defaultBlock>
                          <span class="sg-block__label">{{ block.label || '' }}</span>
                        </ng-template>
                      </ng-container>
                    </div>
                  </ng-container>

                  <ng-template #emptyCell>
                    <ng-container *ngIf="templateMap.has('emptyCell')">
                      <ng-container *ngTemplateOutlet="templateMap.get('emptyCell')!.template;
                        context: { $implicit: row, date: day.date }">
                      </ng-container>
                    </ng-container>
                  </ng-template>
                </ng-container>
              </td>
            </tr>
          </ng-container>

          <!-- Empty group message -->
          <tr *ngIf="!section.collapsed && section.rows.length === 0 && section.group" class="sg-empty-row">
            <td [attr.colspan]="1 + infoColumns.length + dayColumns.length" class="sg-empty-cell">
              <span class="text-muted fst-italic">{{ 'ScheduleGrid.noEmployees' | translate }}</span>
            </td>
          </tr>
        </ng-container>

        <!-- Global empty state -->
        <tr *ngIf="rows.length === 0 && !showLoader">
          <td [attr.colspan]="1 + infoColumns.length + dayColumns.length" class="sg-empty-cell sg-empty-cell--global">
            <div class="text-center py-4">
              <i class="bi bi-calendar-x fs-1 text-muted"></i>
              <p class="text-muted mt-2 mb-0">{{ 'ScheduleGrid.noData' | translate }}</p>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>
